import { useEffect, useState } from "react";
import StatusChip from "../../components/purchase/StatusChip";
import { ICDropDown } from "../../core-component/form";
import { AdvanceSearch, ICButton, ICTable } from "../../core-component/ui";
import { IOption, IPagination } from "../../data/AppInterface";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { cn } from "../../lib";
import {
  CREDIT_TYPE_LABEL_KEY,
  LEDGER_REASON_CHIP_CLASS,
  LEDGER_REASON_LABEL_KEY,
} from "../../utils/PurchaseConstants";
import { formatCreditDelta, formatCredits } from "../../utils/PurchaseFormat";
import { getFirmNameDdl } from "../firm-purchased-subscription/FirmPurchasedSubscriptionViewController";
import CreditGrantForm from "./CreditGrantForm";
import {
  fetchFirmCreditBalance,
  fetchFirmCreditLedger,
  getCreditLedgerFields,
  ICreditLedgerRow,
  IFirmCreditBalance,
} from "./CreditGrantViewController";

interface ICreditChangeCellProps {
  row: ICreditLedgerRow;
}

const CreditChangeCell: React.FC<ICreditChangeCellProps> = ({ row }) => (
  <span
    className={cn("font-semibold", {
      "text-success": (row.delta ?? 0) > 0,
      "text-danger": (row.delta ?? 0) < 0,
    })}
  >
    {formatCreditDelta(row.delta)}
  </span>
);

const CreditGrantView = () => {
  const { t } = UseTranslationHook();
  const { pagination, setChangePagination } = UsePaginationHook();
  const [firmOptions, setFirmOptions] = useState<IOption<string>[]>([]);
  const [firmId, setFirmId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [firmCredit, setFirmCredit] = useState<IFirmCreditBalance>();
  const [ledger, setLedger] = useState<ICreditLedgerRow[]>([]);
  const [showGrantForm, setShowGrantForm] = useState(false);

  useEffect(() => {
    getFirmNameDdl(setFirmOptions, false);
  }, []);

  const loadFirm = async (selectedFirmId: string, page: IPagination) => {
    if (!selectedFirmId) return;
    setIsLoading(true);
    const [balance, rows] = await Promise.all([
      fetchFirmCreditBalance(selectedFirmId),
      fetchFirmCreditLedger(selectedFirmId, page, setChangePagination),
    ]);
    setFirmCredit(balance);
    setLedger(rows);
    setIsLoading(false);
  };

  const reasonCell = (row: ICreditLedgerRow) => (
    <StatusChip
      label={t(LEDGER_REASON_LABEL_KEY[row.reason])}
      className={LEDGER_REASON_CHIP_CLASS[row.reason]}
    />
  );

  const loadLedgerPage = async (page: IPagination) => {
    setIsLoading(true);
    setLedger(await fetchFirmCreditLedger(firmId, page, setChangePagination));
    setIsLoading(false);
  };

  const handleSelectFirm = (selectedFirmId: string) => {
    setFirmId(selectedFirmId);
    loadFirm(selectedFirmId, { ...pagination, currentPage: 1 });
  };

  const handleCloseGrantForm = (status: boolean) => {
    setShowGrantForm(false);
    if (status) loadFirm(firmId, { ...pagination, currentPage: 1 });
  };

  return (
    <div className="w-full flex-1 overflow-scroll scrollbar-remove p-4">
      <AdvanceSearch
        refreshTable={() => loadFirm(firmId, pagination)}
        title={t("credit_grants")}
      />
      <div className="bg-subtle rounded-md p-4 mt-4 grid grid-cols-2 lg:grid-cols-6 gap-4 items-center">
        <div className="col-span-2 lg:col-span-1">
          <ICDropDown
            placeholder={t("firm_name")}
            selected={firmId}
            options={firmOptions}
            searchable
            required
            disabled={isLoading}
            onSelect={(option) => handleSelectFirm(option.data_value)}
            className="z-10"
          />
        </div>
        {firmCredit ? (
          <>
            {firmCredit.balances.map((cell) => (
              <div key={cell.credit_type}>
                <div className="text-xs text-muted">{t(CREDIT_TYPE_LABEL_KEY[cell.credit_type])}</div>
                <div className="text-2xl font-semibold">{formatCredits(t, cell.balance)}</div>
              </div>
            ))}
            <ICButton
              type="button"
              className="!w-fit justify-self-end"
              onClick={() => setShowGrantForm(true)}
            >
              {t("grant_credits")}
            </ICButton>
          </>
        ) : (
          <div className="col-span-2 lg:col-span-5 text-sm text-muted">{t("select_firm_credits")}</div>
        )}
      </div>
      {firmId && (
        <ICTable<ICreditLedgerRow>
          isLoading={isLoading}
          totalFlex="grid grid-cols-14"
          fields={getCreditLedgerFields(
            { reason: reasonCell, change: (row) => <CreditChangeCell row={row} /> },
            t,
          )}
          data={ledger}
          pagination={pagination}
          handleChangePerPageRows={(perPageRows) =>
            loadLedgerPage({ ...pagination, perPageRows, currentPage: 1 })
          }
          handleChangePage={(page) => loadLedgerPage({ ...pagination, currentPage: page })}
          handleSort={() => {}}
        />
      )}
      {showGrantForm && firmCredit && (
        <CreditGrantForm firmCredit={firmCredit} handleClose={handleCloseGrantForm} />
      )}
    </div>
  );
};

export default CreditGrantView;
